import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  faBan,
  faCheck,
  faEye,
  faPen,
  faPlus,
  faPlusCircle,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from "@nebular/theme";
import {
  ColumnModel,
  FilterSettingsModel,
  GridComponent,
  PageSettingsModel,
} from "@syncfusion/ej2-angular-grids";
import { forkJoin } from "rxjs";
import { ErrorService } from '../../../../@core/utils/error.service';
import { KmlService } from "../../../../@core/utils/kml.service";
import { SessionService } from "../../../../@core/utils/session.service";
import {
  ConfirmationData,
  ConfirmationDialogComponent,
} from "../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component";
import { Crop } from '../../models/crop';
import { CropService } from '../../services/crop.service';
import { KwFilterDropdownService } from '../../widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { CropDetailsComponent } from "./crop-details/crop-details.component";

// import { CropListDataSource } from './crop-list-datasource';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  selector: 'crop-list',
  templateUrl: './crop-list.component.html',
  styleUrls: ['./crop-list.component.css']
})
export class CropListComponent implements OnInit {
private destroyRef = inject(DestroyRef);
//#region Properties
public editActive = false;
public cropId: number;
public cropLoading = true;
public submitting: any;
public editCrop: Crop = {};
public faTimes = faTimes;
public faCheck = faCheck;
public allCrops: Crop[] = [];
public allColumns: ColumnModel[] = [];
public savingCropIds: Set<number> = new Set<number>();
public highlightedCropId: number = null;
private pendingScrollToCropId: number = null;
public gridName: string = "CropConfigGrid";
public pageSettings: PageSettingsModel = {
  pageSize: 50,
  pageSizes: [50, 100, 200],
};
public filterSettings: FilterSettingsModel = {
  mode: "Immediate",
  type: "Excel",
};
public toolbarOptions: string[] = [
  "ExcelExport",
];
public enablePersistence: boolean = true;
public filterCount: number = 0;
//#endregion

//#region Icon Properties
public faPlus = faPlus;
public faPen = faPen;
public faEye = faEye;
public faTrash = faTrash;
public faPlusCircle = faPlusCircle;
public faBan = faBan;
//#endregion

//#region ViewChild Properties
@ViewChild("grid", { static: true }) grid: GridComponent;

@ViewChild("actionTemplate", { static: true })
public actionTemplate: any;

@ViewChild("isActiveTemplate", { static: true })
public isActiveTemplate: any;
//#endregion

  constructor(
    private errorService: ErrorService,
    public cropService: CropService,
    private kmlService: KmlService,
    private toastrService: NbToastrService,
    private sessionService: SessionService,
    public dialogService: MatDialog,
    private filterService: KwFilterDropdownService
  ){}

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.cropLoading = true;

    forkJoin([
      this.cropService.getCrops(true),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:
      (results) => {
        this.allCrops = (results[0] || []).slice().sort((a, b) => {
          const an = a && a.name ? a.name.toString().toLowerCase() : '';
          const bn = b && b.name ? b.name.toString().toLowerCase() : '';
          return an.localeCompare(bn);
        });

        this.cropLoading = false;
        this.setColumns();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.cropLoading = false;
      }}
    );
  }

  setColumns() {
    this.allColumns = [];

    // Hidden primary key column - lets us look up/select a specific row
    // (e.g. to scroll to and highlight a newly created crop).
    this.allColumns.push({
      field: "id",
      isPrimaryKey: true,
      visible: false,
    });

    this.allColumns.push(
      ...[
        {
          field: "name",
          headerText: "Crops Name",
        },
        {
          field: "seasonStartMonth",
          headerText: "Season Start Month",
        },
        {
          field: "seasonStartDay",
          headerText: "Season Start Day",
        },
        {
          headerText: "Action",
          template: this.actionTemplate,
          width: 200,
        },
      ]
    );
  }

  clearFilters() {
    window.localStorage.setItem("gridBlocksConfigFilters", "");
    this.grid.clearFiltering();
    this.filterCount = 0;
  }

  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToCropId != null) {
      const id = this.pendingScrollToCropId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToCropId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        // Something can quietly reset the content scroll a moment after
        // this - reassert it once more shortly after to counteract that.
        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  toolbarClick(args) {
    this.cropLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "Excel Export") {
      this.grid
        .excelExport({
          fileName: `CropsExport.xlsx`,
        })
        .then(() => {
          this.cropLoading = false;
        });
    }
  }

  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this crop?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.cropLoading = true;
    this.cropService.deleteCrop(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        // Keep the filter dropdown cache in sync so other pages drop the deleted crop immediately
        this.filterService.removeCached('crop', id);
        this.toastrService.info("Crop successfully deleted!");
        this.cropLoading = false;
        this.loadData();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.cropLoading = false;
      }}
    );
  }
  
  openDialog(IsEdit: boolean, data?: Crop) {
    const cropDetailsDialog = this.dialogService.open(CropDetailsComponent, {
      width: "500px",
    });
    if (IsEdit) {
      var persistData = (this.grid as GridComponent).getPersistData();
      window.localStorage.setItem("grid" + this.gridName, persistData);
      this.sessionService.selectedCrop = { ...data };
    } else {
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedCrop = {} as Crop;
    }

    cropDetailsDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.backgroundSave && result.isNew && result.crop) {
        this.createCropInBackground(result.crop);
      } else if (result && result.backgroundSave && result.crop) {
        this.saveCropInBackground(result.crop);
      } else if (result) {
        this.loadData();
      }
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedCrop = {} as Crop;
    });
  }

  private saveCropInBackground(crop: Crop): void {
    const originalCrop = this.allCrops.find((c) => c.id === crop.id);
    this.savingCropIds.add(crop.id);
    this.insertOrReplaceCropRow(crop);
    this.refreshGridSoon();

    this.cropService.putCrop(crop).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.savingCropIds.delete(crop.id);
        // Keep the filter dropdown cache in sync so other pages show the change immediately
        this.filterService.upsertCached('crop', updated || crop);
        this.insertOrReplaceCropRow(updated || crop);
        this.refreshGridSoon();
        this.toastrService.success(
          "Crop Details successfully updated!",
          crop.name + " updated!"
        );
      },
      error: (err) => {
        this.savingCropIds.delete(crop.id);
        if (originalCrop) {
          this.insertOrReplaceCropRow(originalCrop);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to update Crop "' + crop.name + '". Please try again.',
          "Save failed"
        );
      }
    });
  }

  private createCropInBackground(crop: Crop): void {
    // The crop doesn't exist server-side yet, so show a temporary placeholder
    // row (negative id so it can't collide with a real one) with the saving
    // spinner until the server assigns a real id.
    const tempId = -Date.now();
    this.insertOrReplaceCropRow({ ...crop, id: tempId });
    this.savingCropIds.add(tempId);
    this.refreshGridSoon();

    this.cropService.postCrop(crop).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        // Endpoint is typed/behaves like a list-returning POST for a single record - unwrap defensively (matches putCrop handling).
        const finalCrop: Crop = Array.isArray(created) ? created[0] : (created as unknown as Crop);
        this.savingCropIds.delete(tempId);
        this.filterService.upsertCached('crop', finalCrop);
        this.allCrops = this.allCrops.filter((c) => c.id !== tempId);
        this.insertOrReplaceCropRow(finalCrop || crop);
        this.refreshGridSoon();
        this.jumpToCropRow(finalCrop.id);
        this.toastrService.success(
          "Crop Details successfully saved!",
          (finalCrop && finalCrop.name || crop.name) + " saved!"
        );
      },
      error: (err) => {
        this.savingCropIds.delete(tempId);
        this.allCrops = this.allCrops.filter((c) => c.id !== tempId);
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to create Crop "' + (crop.name || "crop") + '". Please try again.',
          "Create failed"
        );
      }
    });
  }

  private isDefaultSortActive(): boolean {
    const cols = (this.grid && this.grid.sortSettings && this.grid.sortSettings.columns) || [];
    if (cols.length === 0) {
      return true;
    }
    return cols.length === 1 && cols[0].field === "name" && cols[0].direction === "Ascending";
  }

  private jumpToCropRow(id: number): void {
    this.highlightedCropId = id;
    // Scrolling happens once the grid actually finishes its next render
    // (see onGridDataBound) rather than after a guessed delay.
    this.pendingScrollToCropId = id;

    if (!this.grid || !this.isDefaultSortActive()) {
      return;
    }

    // Defer past the refresh(es) already queued by insertOrReplaceCropRow
    // so this pages using the grid's up-to-date data.
    setTimeout(() => {
      const index = this.allCrops.findIndex((c) => c.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allCrops.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
    });
  }

  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }
    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector(".e-content") as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: "auto" });
    } else if (typeof rowElement.scrollIntoView === "function") {
      rowElement.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }

  private startHighlightFade(row: Element, id: number): void {
    if (!row) { return; }
    const rowElement = row as HTMLElement;
    rowElement.classList.add("row-highlight-new");
    if (this.highlightedCropId === id) {
      this.highlightedCropId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove("row-highlight-new");
    }, 2000);
  }

  private insertOrReplaceCropRow(crop: Crop): void {
    const index = this.allCrops.findIndex((c) => c.id === crop.id);
    const next = this.allCrops.slice();
    if (index !== -1) {
      next[index] = crop;
    } else {
      next.push(crop);
    }
    // Keep array order in sync with the grid's default alphabetical display
    // so jumpToCropRow's page-index math lands on the right page.
    next.sort((a, b) => {
      const an = a && a.name ? a.name.toString().toLowerCase() : '';
      const bn = b && b.name ? b.name.toString().toLowerCase() : '';
      return an.localeCompare(bn);
    });
    this.allCrops = next;
    this.refreshGridSoon();
  }

  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingCropIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedCropId != null && args.data.id === this.highlightedCropId) {
      args.row.classList.add('row-highlight-new');
    }
  }
  downloadFiles(args:string) {
    this.cropLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("Excel")) {
      this.grid
        .excelExport({
          fileName: `CropsExport.xlsx`,
        })
        .then(() => {
          this.cropLoading = false;
        });
    }
  }

}
